import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Building2, Save, Loader2, ArrowRight, Upload, Trash2, Palette } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import api, { formatError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import FacebookConnect from '@/components/FacebookConnect';
import { RETURN_AD_KEY } from '@/lib/campaign';

const CATEGORIES = ['Restaurant', 'Retail', 'Beauty & Spa', 'Health & Fitness', 'Education', 'Real Estate', 'Technology', 'Services', 'Fashion', 'Food & Beverage', 'Entertainment', 'Travel', 'Other'];

export default function BusinessProfilePage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  // Set when the customer chose "Connect my Facebook Page" while launching an ad.
  const [returnAd] = useState(() => { try { return sessionStorage.getItem(RETURN_AD_KEY) || ''; } catch { return ''; } });
  const backToAd = () => {
    try { sessionStorage.removeItem(RETURN_AD_KEY); } catch { /* ignore */ }
    navigate(returnAd.startsWith('/') ? returnAd : `/create-ad/${returnAd}`);
  };
  const [profile, setProfile] = useState({ business_name: '', category: '', city: '', website: '', phone: '', description: '', brand_color: '' });
  const [logoBusy, setLogoBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [saves, setSaves] = useState(0); // each save: the Page guide rewrites the Page's details

  useEffect(() => {
    api.get('/api/business-profile').then(({ data }) => {
      if (data && typeof data === 'object') setProfile(p => ({ ...p, ...data }));
    }).catch(() => {}).finally(() => setFetching(false));
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { logo_url, ...fields } = profile; // the logo is saved on its own
      await api.put('/api/business-profile', fields);
      await refreshUser();
      setSaves(n => n + 1);
      toast.success('Business profile saved!');
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || 'Failed to save profile');
    }
    setLoading(false);
  };

  // The logo is saved as soon as it's picked; new ad images use it.
  const uploadLogo = (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('That logo is too big. Use one under 5 MB.'); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      setLogoBusy(true);
      try {
        const { data } = await api.post('/api/business-profile/logo', { image_base64: reader.result });
        setProfile(p => ({ ...p, logo_url: data.logo_url }));
        toast.success('Logo saved. Your next ad images will show it.');
      } catch (err) {
        toast.error(formatError(err.response?.data?.detail));
      }
      setLogoBusy(false);
    };
    reader.readAsDataURL(file);
  };
  const removeLogo = async () => {
    setLogoBusy(true);
    try {
      await api.delete('/api/business-profile/logo');
      setProfile(({ logo_url, ...p }) => p);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
    setLogoBusy(false);
  };

  if (fetching) return <div className="flex justify-center py-20"><div className="flex gap-1"><span className="loading-dot" /><span className="loading-dot" /><span className="loading-dot" /></div></div>;

  return (
    <div className="max-w-xl mx-auto" data-testid="business-profile-page">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-brand border-2 border-ink flex items-center justify-center">
            <Building2 className="w-5 h-5 text-brand-strong" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight font-heading">Your business</h1>
            <p className="text-sm text-muted-foreground">Tell us about your business to create better ads.</p>
          </div>
        </div>
      </div>
      {returnAd && (
        <div className="mb-6 p-3 rounded-xl bg-butter border-2 border-ink shadow-neo-sm flex items-center justify-between gap-3" data-testid="return-to-ad">
          <span className="text-sm">{user?.ad_page && !user.ad_page.fallback
            ? 'Your Facebook Page is ready. Go back to your ad: WhatsApp messages work now.'
            : 'Your ad is saved. Set up your Facebook Page below, then go back to it.'}</span>
          <Button size="sm" variant="outline" onClick={backToAd}>Back to your ad <ArrowRight className="w-3 h-3 ml-1" /></Button>
        </div>
      )}

      <motion.form initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} onSubmit={handleSave}
        className="space-y-5 p-6 rounded-2xl bg-card border-2 border-ink shadow-neo">
        <div>
          <Label className="text-sm font-semibold">Business Name</Label>
          <Input value={profile.business_name} onChange={e => setProfile(p => ({ ...p, business_name: e.target.value }))}
            placeholder="Your Business Name" className="mt-1.5" data-testid="business-name-input" />
        </div>
        <div>
          <Label className="text-sm font-semibold">Category</Label>
          <Select value={profile.category} onValueChange={v => setProfile(p => ({ ...p, category: v }))}>
            <SelectTrigger className="mt-1.5" data-testid="category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
            <SelectContent>
              {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-sm font-semibold">City</Label>
            <Input value={profile.city} onChange={e => setProfile(p => ({ ...p, city: e.target.value }))} placeholder="Mumbai, Delhi, etc." className="mt-1.5" data-testid="city-input" />
          </div>
          <div>
            <Label className="text-sm font-semibold">Phone</Label>
            <Input value={profile.phone} onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))} placeholder="+91 9876543210" className="mt-1.5" data-testid="phone-input" />
          </div>
        </div>
        <div>
          <Label className="text-sm font-semibold">Website</Label>
          <Input value={profile.website} onChange={e => setProfile(p => ({ ...p, website: e.target.value }))} placeholder="https://yourbusiness.com" className="mt-1.5" data-testid="website-input" />
        </div>
        <div>
          <Label className="text-sm font-semibold">Description</Label>
          <Textarea value={profile.description} onChange={e => setProfile(p => ({ ...p, description: e.target.value }))}
            placeholder="A short description of what your business does, your unique selling points..." rows={3} className="mt-1.5" data-testid="description-input" />
        </div>
        <div className="pt-1 border-t-2 border-dashed border-ink/20" data-testid="brand-section">
          <p className="text-sm font-semibold mt-4 flex items-center gap-1.5"><Palette className="w-4 h-4" aria-hidden="true" /> Your brand on ads</p>
          <p className="text-xs text-muted-foreground mb-3">Your logo and colour go on your ad images, so they look like yours, not a template.</p>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-20 h-20 rounded-xl border-2 border-ink/20 bg-white flex items-center justify-center overflow-hidden shrink-0">
              {profile.logo_url
                ? <img src={`${process.env.REACT_APP_BACKEND_URL}${profile.logo_url}`} alt="Your logo" className="max-w-full max-h-full object-contain p-1.5" />
                : <span className="text-[10px] text-muted-foreground text-center px-1">No logo</span>}
            </div>
            <div className="flex flex-wrap gap-2">
              <label className={`inline-flex items-center h-9 px-3 rounded-md border-2 border-ink text-sm font-medium bg-card hover:bg-accent ${logoBusy ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}`} data-testid="logo-upload">
                {logoBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
                {profile.logo_url ? 'Change logo' : 'Upload logo'}
                <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                  onChange={(e) => { uploadLogo(e.target.files?.[0]); e.target.value = ''; }} />
              </label>
              {profile.logo_url && (
                <Button type="button" variant="ghost" size="sm" className="h-9" disabled={logoBusy} onClick={removeLogo}>
                  <Trash2 className="w-4 h-4 mr-1" /> Remove
                </Button>
              )}
            </div>
          </div>
          <Label className="text-sm font-semibold" htmlFor="brand-color">Brand colour</Label>
          <div className="flex items-center gap-2 mt-1.5">
            <input id="brand-color" type="color" value={profile.brand_color || '#FF7A1A'}
              onChange={e => setProfile(p => ({ ...p, brand_color: e.target.value.toUpperCase() }))}
              className="w-11 h-11 rounded-md border-2 border-ink cursor-pointer bg-card p-0.5" aria-label="Pick your brand colour" />
            <Input value={profile.brand_color || ''} onChange={e => setProfile(p => ({ ...p, brand_color: e.target.value.trim() }))}
              placeholder="Taken from each photo" className="max-w-[11rem] font-mono" data-testid="brand-color-input" />
            {profile.brand_color && (
              <Button type="button" variant="ghost" size="sm" onClick={() => setProfile(p => ({ ...p, brand_color: '' }))}>Use photo colours</Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-1.5">PNG logos with a clear background look best. Leave the colour empty to match each photo.</p>
        </div>
        <Button type="submit" disabled={loading} className="w-full gradient-bg h-11" data-testid="save-profile-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
          Save Profile
        </Button>
      </motion.form>
      <FacebookConnect onBackToAd={returnAd ? backToAd : null} profileVersion={saves} />
    </div>
  );
}
